import Scrollspy from 'react-scrollspy-ez';
import styled from 'styled-components';

import BadgeStyled from '../../../components/Badge';
import ContentHeaderStyled from '../../../components/ContentHeader';
import ExternalLinkGroupStyled from '../../../components/ExternalLinkGroup';
import PaginationStyled from '../../../components/Pagination';
import { REFERENCE_LINKS } from '../../../constants';
import { IconDanger, IconSuccess } from '../../../core';
import commonPageContentStyles from '../../../styles/mixins/_commonPageContentStyles';
import navigationStyles from '../../../styles/mixins/navigationMenuStyles';

interface Props {
  className?: string;
}

const dataExternalLinkGroups = [
  {
    id: 1,
    title: 'Government accessibility resources',
    cards: [
      {
        title: 'Find your Section 508 Coordinator',
        subtitle: '[section508.gov]',
        linkUrl: REFERENCE_LINKS.section508ProgramManagerListing,
      },
      {
        title: 'Plain language resources',
        subtitle: '[digital.gov]',
        linkUrl: REFERENCE_LINKS.digitalGovPlainLanguageGuide,
      },
      {
        title: '18F’s accessibility guide ',
        subtitle: '[guides.18f.org]',
        linkUrl: REFERENCE_LINKS.eighteenFAccessibilityGuide,
      },
    ],
  },
];

const FormElementsStructure = ({ className = '' }: Props) => (
  <div className={className}>
    <div className="content">
      <ContentHeaderStyled
        title="Form Elements"
        breadcrumbs={[
          { label: 'General Components', url: '/general-components' },
          { label: 'Form Elements' },
        ]}
      />
      <div className="page-container">
        <div className="page-content">
          <div className="page-content-with-navigation-wrapper">
            <div className="page-content-with-navigation">
              <div className="page-content-item">
                <h2 className="h1" id="overview">
                  Overview
                </h2>
                <h3 className="h3" id="usage">
                  Usage
                </h3>
                <p className="mb-40">
                  Form elements are essential for user interaction, enabling data input and
                  selection. They should be intuitive, accessible, and consistent throughout the
                  interface. Proper usage ensures clear labels, logical grouping, and sufficient
                  spacing for readability and ease of interaction. Forms should guide users
                  smoothly, minimizing errors and enhancing the overall experience.
                </p>
                <h3 className="h3" id="behaviors">
                  Behaviors
                </h3>
                <p className="mb-40">
                  Form elements should respond intuitively to user interactions, providing clear
                  feedback for focus, hover, error, and success states. Required or optional labels
                  should be used based on form complexity, ensuring clarity for users. Interactive
                  elements must be easily clickable, with appropriate spacing to prevent misclicks,
                  while disabled fields should be visibly distinct and non-interactive.
                </p>
                <h2 className="h1" id="specifications">
                  Specifications
                </h2>
                <h3 className="h3" id="anatomy">
                  Anatomy
                </h3>
                <p className="mb-40">
                  The anatomy of form elements consists of three key sections: the Header, which
                  includes an optional title and description to provide context; the Form Body,
                  where users enter information through various input fields such as text fields and
                  checkboxes, supported by labels and helper text to ensure accuracy; and the
                  Footer, which contains actions like submit or cancel, allowing users to finalize
                  their input. Each component is designed for clarity, accessibility, and a seamless
                  user experience.
                </p>
              </div>
              <img
                src="/images/general-components-form-elements/screen-alignement@2x.webp"
                width={1000}
                height={726}
                alt=""
                className="mb-40 img-responsive"
              />
              <div className="page-content-item">
                <h3 className="h3" id="alignment">
                  Alignment
                </h3>
                <p className="mb-40 text-paragraph-medium">
                  Form elements should be consistently aligned to enhance readability and usability.
                  Input components should vertically align with all other form elements, ensuring a
                  clear and structured layout. Default forms typically use a wide gutter mode,
                  aligning flush to grid columns while prioritizing vertical label alignment. In
                  contrast, fluid forms use a condensed grid, allowing components to extend into the
                  gutter without disrupting label alignment. Maintaining a structured alignment
                  helps create a cohesive design, ensuring forms remain intuitive and visually
                  balanced.
                </p>
              </div>
              <div className="ex-cards">
                <div className="ex-card true">
                  <img
                    className="ex-card-img"
                    src="/images/general-components-form-elements/screen-alignment-01@2x.webp"
                    width={488}
                    height={437}
                    alt=""
                  />
                  <div className="ex-card-desc">
                    <BadgeStyled className="success" iconLeft={<IconSuccess />}>
                      Do
                    </BadgeStyled>
                    <p>
                      Do align default form inputs and labels to the grid columns using the wide
                      gutter mode.
                    </p>
                  </div>
                </div>
                <div className="ex-card false">
                  <img
                    className="ex-card-img"
                    src="/images/general-components-form-elements/screen-alignment-02@2x.webp"
                    width={488}
                    height={437}
                    alt=""
                  />
                  <div className="ex-card-desc">
                    <BadgeStyled className="danger" iconLeft={<IconDanger />}>
                      Don&apos;t
                    </BadgeStyled>
                    <p>
                      Don&apos;t hang default inputs into the grid gutters. Doing so will cause
                      labels to misalign with other form elements.
                    </p>
                  </div>
                </div>
              </div>
              <h2 className="h1 divider" id="guidelines">
                Guidelines
              </h2>
              <div className="page-content-item">
                <h3 className="h3" id="form-controls">
                  Form controls
                </h3>
                <p className="mb-40 text-paragraph-medium">
                  Form controls provide users with interactive elements to input and modify data.
                  These include text fields, checkboxes, radio buttons, dropdowns, and toggles, each
                  designed for specific data entry needs. Proper spacing, alignment, and
                  accessibility considerations ensure usability and consistency across different
                  form layouts.
                </p>
              </div>
              <table className="table table-variants mb-40">
                <thead>
                  <tr>
                    <th style={{ width: '15.625rem' }}>State</th>
                    <th>When to use</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td valign="top">Free form inputs</td>
                    <td>
                      When a text input is live but a user is not directly interacting with it. This
                      is commonly referred to as the default or normal state of the component. An
                      enabled text input field can contain no content, placeholder text, or user
                      generate content.
                    </td>
                  </tr>
                  <tr>
                    <td valign="top">Selection controls</td>
                    <td>
                      Selection controls offer users a selection from pre-determined options. Common
                      selection controls include: checkboxes, radio buttons, file uploaders,
                      toggles, and select lists (combo box and multiselect).
                    </td>
                  </tr>
                  <tr>
                    <td valign="top">Bound entry controls</td>
                    <td>
                      Selection controls offer users a selection from pre-determined options. Common
                      selection controls include: checkboxes, radio buttons, file uploaders,
                      toggles, and select lists (combo box and multiselect).
                    </td>
                  </tr>
                </tbody>
              </table>
              <div className="page-content-item">
                <h3 className="h3" id="states">
                  States
                </h3>
                <p className="mb-40 text-paragraph-medium">
                  The following are common states that can be applied to components when used in a
                  form.
                </p>
              </div>
              <div className="number-cards row-gap-24">
                <div className="number-card">
                  <div className="number-card-img">
                    <span className="number-circle">1</span>
                    <img
                      src="/images/general-components-form-elements/screen-states-01@2x.webp"
                      width={488}
                      height={313}
                      alt=""
                    />
                  </div>
                </div>
                <div className="number-card">
                  <div className="number-card-img">
                    <span className="number-circle">2</span>
                    <img
                      src="/images/general-components-form-elements/screen-states-02@2x.webp"
                      width={488}
                      height={313}
                      alt=""
                    />
                  </div>
                </div>
                <div className="number-card">
                  <div className="number-card-img">
                    <span className="number-circle">3</span>
                    <img
                      src="/images/general-components-form-elements/screen-states-03@3x.jpg"
                      alt=""
                    />
                  </div>
                </div>
                <div className="number-card">
                  <div className="number-card-img">
                    <span className="number-circle">4</span>
                    <img
                      src="/images/general-components-form-elements/screen-states-04@3x.jpg"
                      alt=""
                    />
                  </div>
                </div>
                <div className="number-card">
                  <div className="number-card-img">
                    <span className="number-circle">5</span>
                    <img
                      src="/images/general-components-form-elements/screen-states-05@2x.webp"
                      width={488}
                      height={313}
                      alt=""
                    />
                  </div>
                </div>
              </div>
              <table className="table table-variants mb-40">
                <thead>
                  <tr>
                    <th style={{ width: '15.625rem' }}>Type</th>
                    <th>Overview</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td valign="top">1. Enabled</td>
                    <td>
                      Free-form text inputs are the most commonly used components in forms. They
                      allow a user to enter any configuration of letters or numbers. Common
                      free-form inputs include: text input and text area.
                    </td>
                  </tr>
                  <tr>
                    <td valign="top">2. Active</td>
                    <td>When a user is actively typing content into the field.</td>
                  </tr>
                  <tr>
                    <td valign="top">3. Focus</td>
                    <td>
                      When a user tabs to or clicks on the text input, it becomes focused,
                      indicating the user has successfully navigated to the component.
                    </td>
                  </tr>
                  <tr>
                    <td valign="top">4. Disabled</td>
                    <td>
                      When the user is not allowed to interact with the text input due to either
                      permissions, dependencies, or pre-requisites. The disabled state completely
                      removes the interactive function from a component. The styling is not subject
                      to WCAG contrast compliance.
                    </td>
                  </tr>
                </tbody>
              </table>
              <div className="page-content-item mb-40">
                <h3 className="h3" id="button-positioning">
                  Button positioning
                </h3>
                <p className="mb-8 text-paragraph-medium">
                  Button positioning in forms should guide users toward the primary action while
                  maintaining clarity and alignment. Primary buttons, such as &quot;Submit&quot; or
                  &quot;Save,&quot; should be placed at the end of the form, aligned consistently
                  with the input fields. Secondary actions, like &quot;Cancel&quot; or
                  &quot;Reset,&quot; should be visually distinct and positioned to avoid accidental
                  selection. Proper spacing and alignment enhance usability and ensure a smooth user
                  experience.
                </p>
                <p className="mb-40 text-paragraph-medium">
                  Buttons are sorted by importance from right to left, with the most important
                  button (primary) on the right.
                </p>
              </div>
              <img
                src="/images/general-components-form-elements/screen-button-positioning-01@2x.webp"
                width={1000}
                height={482}
                alt=""
                className="mb-40 img-responsive"
              />
              <div className="page-content-item mb-40">
                <p className="text-paragraph-medium">
                  Exceptions to this are single-page forms, flags, and section messages where
                  buttons are left aligned with the primary button on the left.
                </p>
              </div>
              <img
                src="/images/general-components-form-elements/screen-button-positioning-02@2x.webp"
                width={1000}
                height={793}
                alt=""
                className="mb-40 img-responsive"
              />
              <h2 className="h1 divider" id="references">
                References
              </h2>
              {dataExternalLinkGroups.map((externalLinkGroup) => (
                <ExternalLinkGroupStyled
                  key={externalLinkGroup.id}
                  title={externalLinkGroup.title}
                  cards={externalLinkGroup.cards}
                />
              ))}
            </div>
            <div>
              <div className="page-navigation">
                <h2>Contents</h2>
                <Scrollspy
                  ids={[
                    'overview',
                    'usage',
                    'behaviors',
                    'specifications',
                    'anatomy',
                    'alignment',
                    'guidelines',
                    'form-controls',
                    'states',
                    'references',
                  ]}
                  offset={0}
                  activeItemClassName="active"
                  itemContainerClassName="multilevel-link-list"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <PaginationStyled
        previousLink="/general-components/links"
        previous="Links"
        nextLink="/general-components/communication"
        next="Communication"
      />
    </div>
  </div>
);

const FormElements = styled(FormElementsStructure)`
  ${commonPageContentStyles}
  ${navigationStyles([2, 3, 5, 6, 8, 9])}

    overflow: unset;
  height: 100%;

  > .content {
    width: 100%;
    padding: 0;
  }
`;

export default FormElements;
